import React, { useState } from 'react';
import axios from 'axios';

const Login = () => {
  const [username, setUsername] = useState(
    () => localStorage.getItem('rememberedUser') || ''
  );
  const [password, setPassword] = useState('');
  const [remember, setRemember] = useState(
    () => !!localStorage.getItem('rememberedUser')
  );
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);

  // One place for every message the form shows. `form` is the banner above
  // the fields; the others sit under their own input.
  const [errors, setErrors] = useState({ form: '', username: '', password: '' });

  const togglePassword = () => setShowPassword((prev) => !prev);

  // Clearing as the user types keeps a stale error from sitting under a field
  // they have already fixed.
  const onUsernameChange = (e) => {
    setUsername(e.target.value);
    if (errors.username || errors.form) {
      setErrors((prev) => ({ ...prev, username: '', form: '' }));
    }
  };

  const onPasswordChange = (e) => {
    setPassword(e.target.value);
    if (errors.password || errors.form) {
      setErrors((prev) => ({ ...prev, password: '', form: '' }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (loading) return;

    const nextErrors = { form: '', username: '', password: '' };
    if (!username.trim()) nextErrors.username = 'Please enter your username or email.';
    if (!password) nextErrors.password = 'Please enter your password.';

    if (nextErrors.username || nextErrors.password) {
      setErrors(nextErrors);
      return;
    }

    setErrors(nextErrors);
    setLoading(true);

    try {
      const res = await axios.post('/api/login', { username: username.trim(), password });

      // Consumers (Navbar, Sidebar, Dashboard, Users) read the whole
      // { token, user } payload back out and reach for `.user`, so the full
      // response object has to be what gets stored here.
      localStorage.setItem('token', res.data.token);
      localStorage.setItem('user', JSON.stringify(res.data));

      if (remember) {
        localStorage.setItem('rememberedUser', username.trim());
      } else {
        localStorage.removeItem('rememberedUser');
      }

      window.location.href = '/dashboard';
    } catch (err) {
      const status = err.response?.status;
      // The rate limiter (429) sends back its own wait-and-retry text, which
      // is more useful than a generic failure message.
      const message =
        status === 429
          ? err.response?.data?.message || 'Too many login attempts. Please try again later.'
          : status === 401 || status === 400
          ? 'The username or password you entered is incorrect.'
          : 'We could not reach the server. Please try again.';

      setErrors({ form: message, username: '', password: '' });
      setLoading(false);
    }
  };

  const fieldBase =
    'text-bgray-800 text-base border h-12 w-full rounded-lg px-4 py-2.5 transition-colors ' +
    'focus:outline-none focus:ring-2 focus:ring-opacity-40 ' +
    'dark:bg-darkblack-500 dark:text-white ' +
    'placeholder:text-bgray-500 placeholder:text-base';

  const fieldTone = (hasError) =>
    hasError
      ? ' border-red-400 focus:border-red-400 focus:ring-red-200'
      : ' border-bgray-300 dark:border-darkblack-400 focus:border-success-300 focus:ring-green-200';

  return (
    <section className="bg-white dark:bg-darkblack-500">
      <div className="flex flex-col lg:flex-row min-h-screen">
        {/* Form side - sits on the right from lg up, see the order classes */}
        <div className="w-full lg:w-1/2 lg:order-2 px-5 xl:px-12 flex items-center justify-center">
          <div className="w-full max-w-[450px] py-16">
            <header className="text-center mb-8">
              <h2 className="text-bgray-900 dark:text-white text-4xl font-semibold font-poppins mb-2">
                Sign in to Tera<span className="span-color">Stamp</span>
              </h2>
              <p className="font-urbanis text-base font-medium text-bgray-600 dark:text-bgray-50">
                A Data Management Reporting Platform
              </p>
            </header>

            <form onSubmit={handleSubmit} noValidate>
              {errors.form && (
                <div
                  role="alert"
                  className="mb-6 flex items-start gap-3 rounded-lg bg-error-50 px-4 py-3"
                >
                  <svg
                    className="mt-0.5 shrink-0"
                    width="18"
                    height="18"
                    viewBox="0 0 20 20"
                    fill="none"
                    aria-hidden="true"
                  >
                    <circle cx="10" cy="10" r="9" stroke="#dd3333" strokeWidth="1.5" />
                    <path d="M10 5.5v5" stroke="#dd3333" strokeWidth="1.5" strokeLinecap="round" />
                    <circle cx="10" cy="14" r="1" fill="#dd3333" />
                  </svg>
                  <span className="text-error-300 text-sm font-medium">{errors.form}</span>
                </div>
              )}

              <div className="mb-4">
                <label
                  htmlFor="username"
                  className="block mb-2 text-sm font-semibold text-bgray-900 dark:text-white"
                >
                  Username or email
                </label>
                <input
                  id="username"
                  name="username"
                  type="text"
                  autoComplete="username"
                  autoFocus
                  value={username}
                  onChange={onUsernameChange}
                  aria-invalid={!!errors.username}
                  aria-describedby={errors.username ? 'username-error' : undefined}
                  className={fieldBase + fieldTone(!!errors.username)}
                  placeholder="you@company.com"
                />
                {errors.username && (
                  <p id="username-error" className="mt-2 text-sm text-red-600">
                    {errors.username}
                  </p>
                )}
              </div>

              <div className="mb-6">
                <label
                  htmlFor="password"
                  className="block mb-2 text-sm font-semibold text-bgray-900 dark:text-white"
                >
                  Password
                </label>
                <div className="relative">
                  <input
                    id="password"
                    name="password"
                    type={showPassword ? 'text' : 'password'}
                    autoComplete="current-password"
                    value={password}
                    onChange={onPasswordChange}
                    aria-invalid={!!errors.password}
                    aria-describedby={errors.password ? 'password-error' : undefined}
                    className={fieldBase + fieldTone(!!errors.password) + ' pr-12'}
                    placeholder="Enter your password"
                  />
                  <button
                    type="button"
                    onClick={togglePassword}
                    aria-label={showPassword ? 'Hide password' : 'Show password'}
                    className="absolute top-0 right-0 h-full px-4 flex items-center text-bgray-500 hover:text-bgray-700 dark:hover:text-white transition-colors"
                  >
                    {showPassword ? (
                      // Eye open
                      <svg width="22" height="20" viewBox="0 0 22 20" fill="none" aria-hidden="true">
                        <path
                          d="M11 3C15 3 18.333 5.333 21 10C18.333 14.667 15 17 11 17C7 17 3.667 14.667 1 10C3.667 5.333 7 3 11 3Z"
                          stroke="currentColor"
                          strokeWidth="1.5"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                        <circle cx="11" cy="10" r="3" stroke="currentColor" strokeWidth="1.5" />
                      </svg>
                    ) : (
                      // Eye struck through
                      <svg width="22" height="20" viewBox="0 0 22 20" fill="none" aria-hidden="true">
                        <path
                          d="M2 1L20 19"
                          stroke="currentColor"
                          strokeWidth="1.5"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                        <path
                          d="M9.58445 8.58704C9.20917 8.96205 8.99823 9.47079 8.99805 10.0013C8.99786 10.5319 9.20844 11.0408 9.58345 11.416C9.95847 11.7913 10.4672 12.0023 10.9977 12.0024C11.5283 12.0026 12.0372 11.7921 12.4125 11.417"
                          stroke="currentColor"
                          strokeWidth="1.5"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                        <path
                          d="M8.363 3.36506C9.22042 3.11978 10.1082 2.9969 11 3.00006C15 3.00006 18.333 5.33306 21 10.0001C20.222 11.3611 19.388 12.5241 18.497 13.4881M16.357 15.3491C14.726 16.4491 12.942 17.0001 11 17.0001C7 17.0001 3.667 14.6671 1 10.0001C2.369 7.60506 3.913 5.82506 5.632 4.65906"
                          stroke="currentColor"
                          strokeWidth="1.5"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                    )}
                  </button>
                </div>
                {errors.password && (
                  <p id="password-error" className="mt-2 text-sm text-red-600">
                    {errors.password}
                  </p>
                )}
              </div>

              <div className="flex justify-between items-center mb-7">
                <div className="flex items-center space-x-3">
                  <input
                    type="checkbox"
                    className="w-5 h-5 dark:bg-darkblack-500 focus:ring-transparent rounded border border-bgray-300 focus:accent-success-300 text-success-300"
                    name="remember"
                    id="remember"
                    checked={remember}
                    onChange={(e) => setRemember(e.target.checked)}
                  />
                  <label
                    htmlFor="remember"
                    className="text-bgray-900 dark:text-white text-base font-semibold cursor-pointer"
                  >
                    Remember me
                  </label>
                </div>
              </div>

              <button
                type="submit"
                disabled={loading}
                className={
                  'py-2.5 flex items-center justify-center text-white text-base font-semibold rounded-lg w-full transition-all ' +
                  (loading
                    ? 'bg-success-400 opacity-70 cursor-not-allowed'
                    : 'bg-success-300 hover:bg-success-400')
                }
              >
                {loading ? 'Signing in...' : 'Sign In'}
              </button>
            </form>

            <p className="text-bgray-600 dark:text-white text-center text-sm mt-10">
              @ 2025 Terastamp. All Right Reserved.
            </p>
          </div>
        </div>

        {/* Brand side - decorative, so it drops away on small screens */}
        <div className="hidden lg:flex lg:w-1/2 lg:order-1 bg-gradient-to-br from-green-500 to-emerald-800 items-center justify-center px-12">
          <div className="max-w-[440px] text-white">
            <div className="text-5xl font-bold mb-6">
              Tera<span className="text-green-200">Stamp</span>
            </div>
            <p className="text-xl font-medium text-green-50 mb-10 leading-relaxed">
              Turn device telemetry into reports your team can act on.
            </p>
            <ul className="space-y-5">
              {[
                'Live dashboards across every connected device',
                'Scheduled reports exported to Word and PDF',
                'Historical trends and graphs on demand',
              ].map((item) => (
                <li key={item} className="flex items-start gap-3">
                  <svg
                    className="mt-1 shrink-0"
                    width="20"
                    height="20"
                    viewBox="0 0 20 20"
                    fill="none"
                    aria-hidden="true"
                  >
                    <circle cx="10" cy="10" r="10" fill="rgba(255,255,255,0.2)" />
                    <path
                      d="M6 10.5L8.5 13L14 7.5"
                      stroke="white"
                      strokeWidth="1.8"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                  <span className="text-base text-green-50">{item}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Login;
